import fs from 'fs';
import os from 'os';
import path from 'path';

import { error, hint } from './output.ts';

const CREDENTIALS_PATH = path.join(os.homedir(), '.sortlist', 'credentials.json');
const ELEVATED_CREDENTIALS_PATH = path.join(os.homedir(), '.sortlist', 'elevated-credentials.json');

export const ELEVATION_MAX_SECONDS = 30 * 60;

export interface Credentials {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
}

export type ElevationLevel = 'sales' | 'admin' | 'superadmin';

export interface ElevatedCredentials {
  accessToken: string;
  expiresAt: number;
  level: ElevationLevel;
}

function ensureDir(): void {
  const dir = path.dirname(CREDENTIALS_PATH);
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
}

export function saveCredentials(creds: Credentials): void {
  ensureDir();
  fs.writeFileSync(CREDENTIALS_PATH, JSON.stringify(creds, null, 2) + '\n', { mode: 0o600 });
}

export function loadCredentials(): Credentials | null {
  try {
    const raw = fs.readFileSync(CREDENTIALS_PATH, 'utf-8');
    return JSON.parse(raw) as Credentials;
  } catch {
    return null;
  }
}

export function clearCredentials(): void {
  try {
    fs.unlinkSync(CREDENTIALS_PATH);
  } catch {
    // already gone
  }
}

export function isExpired(creds: Pick<Credentials, 'expiresAt'>): boolean {
  // 60s buffer before actual expiry
  return Date.now() >= (creds.expiresAt - 60) * 1000;
}

export function saveElevatedCredentials(
  accessToken: string,
  expiresIn: number,
  level: ElevationLevel,
): ElevatedCredentials {
  const creds: ElevatedCredentials = {
    accessToken,
    expiresAt: Math.floor(Date.now() / 1000) + Math.min(expiresIn, ELEVATION_MAX_SECONDS),
    level,
  };
  ensureDir();
  fs.writeFileSync(ELEVATED_CREDENTIALS_PATH, JSON.stringify(creds, null, 2) + '\n', { mode: 0o600 });

  return creds;
}

export function loadElevatedCredentials(): ElevatedCredentials | null {
  let creds: ElevatedCredentials;
  try {
    creds = JSON.parse(fs.readFileSync(ELEVATED_CREDENTIALS_PATH, 'utf-8'));
  } catch {
    return null;
  }

  if (isExpired(creds)) {
    clearElevatedCredentials();
    return null;
  }

  return creds;
}

export function clearElevatedCredentials(): boolean {
  try {
    fs.unlinkSync(ELEVATED_CREDENTIALS_PATH);
    return true;
  } catch {
    return false;
  }
}

export function storedUserAccessToken(): string | null {
  return loadElevatedCredentials()?.accessToken ?? loadCredentials()?.accessToken ?? null;
}

export function ensureAuthenticated(): Credentials {
  const creds = loadCredentials();
  if (!creds) {
    error('You are not logged in.');
    hint('Run `sl login` to authenticate with your Sortlist account.');
    process.exit(2);
  }
  return creds;
}
